import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, Globe, Layers } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDebounce } from "../../hooks/useDebounce";
import { SearchAPI, SearchResultDto, SearchResult } from "../../api/searchApi";
import { useSearch } from "../../contexts/SearchContext";

const SearchDialog = () => {
  const { isSearchOpen, closeSearch } = useSearch();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedQuery = useDebounce(query, 1000);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [searchInfo, setSearchInfo] = useState<SearchResultDto | null>(null);
  const [isMultiSearch, setIsMultiSearch] = useState(false);
  const [isMultiSearchLoading, setIsMultiSearchLoading] = useState(false);
  const [multiSearchResults, setMultiSearchResults] = useState<{ [term: string]: SearchResult[] }>({});
  const [selectedParts, setSelectedParts] = useState<{ [term: string]: string }>({});
  const location = useLocation();

  // Очищаем поиск при изменении URL
  useEffect(() => {
    if (location.pathname === '/product') {
      setQuery("");
      setResults([]);
      setSearchCompleted(false);
      setSearchInfo(null);
      setIsMultiSearch(false);
      setIsMultiSearchLoading(false);
    }
  }, [location]);

  // Определяем, является ли поиск мультипоиском
  useEffect(() => {
    const hasSpaces = query.includes(' ');
    const hasMultipleLines = query.includes('\n') || query.includes('\r');
    const hasTabs = query.includes('\t');
    
    setIsMultiSearch(hasSpaces || hasMultipleLines || hasTabs);
  }, [query]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!debouncedQuery.trim()) {
        setResults([]);
        setSearchCompleted(false);
        setSearchInfo(null);
        return;
      }

      setLoading(true);
      setSearchCompleted(false);

      try {
        // Поиск с типом (сначала в базе, потом глобальный)
        const searchResult = await SearchAPI.getProductsWithType(debouncedQuery);
        setSearchInfo(searchResult);
        setResults(searchResult.parts || []);
      } catch (error) {
        console.error("Ошибка загрузки данных", error);
        setResults([]);
        setSearchInfo(null);
      } finally {
        setLoading(false);
        setSearchCompleted(true);
      }
    };

    fetchProducts();
  }, [debouncedQuery]);

  const handleClose = () => {
    setQuery(""); // Очистка строки поиска при закрытии
    setResults([]);
    setSearchCompleted(false);
    setSearchInfo(null);
    closeSearch();
  };

  const handleGlobalSearch = () => {
    navigate(`/global-search?q=${encodeURIComponent(query)}`);
    handleClose();
  };

  const handleMultiSearch = async () => {
    if (!query.trim()) return;
    
    setIsMultiSearchLoading(true);
    
    try {
      // Парсим артикулы из строки поиска
      const searchTerms = query
        .split(/[\s\n\r\t]+/)
        .map(term => term.trim())
        .filter(term => term.length > 0);
      
      // Загружаем результаты мультипоиска
      const multiResults = await SearchAPI.getMultiSearchResults(searchTerms);
      
      // Преобразуем результаты в нужный формат
      const formattedResults: { [term: string]: SearchResult[] } = {};
      Object.keys(multiResults).forEach(term => {
        formattedResults[term] = multiResults[term].parts || [];
      });
      
      setMultiSearchResults(formattedResults);
      setSelectedParts({}); // Сбрасываем выбор
    } catch (error) {
      console.error("Ошибка мультипоиска:", error);
    } finally {
      setIsMultiSearchLoading(false);
    }
  };

  const navigate = useNavigate();

  const handleSelectProduct = (item: SearchResult) => {
    navigate(`/product?part_id=${item.uniqueId}`);
    handleClose();
  };

  const handleSelectPart = (term: string, uniqueId: string) => {
    setSelectedParts(prev => ({
      ...prev,
      [term]: uniqueId
    }));
  };

  const handleProceedToMultiSearch = () => {
    const searchTerms = Object.keys(selectedParts);
    const selectedUniqueIds = Object.values(selectedParts);
    
    // Переходим на страницу мультипоиска с выбранными запчастями
    navigate(`/multi-search?terms=${encodeURIComponent(JSON.stringify(searchTerms))}&selected=${encodeURIComponent(JSON.stringify(selectedUniqueIds))}`);
    handleClose();
  };

  const isAllPartsSelected = () => {
    const searchTerms = Object.keys(multiSearchResults);
    return searchTerms.length > 0 && searchTerms.every(term => selectedParts[term]);
  };

  return (
    <AnimatePresence>
      {isSearchOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] backdrop-blur-md"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="relative w-[98vw] max-w-screen-sm rounded-lg border bg-white text-popover-foreground shadow-lg p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <Search className="size-4 shrink-0 text-muted-foreground" />
              <input
                autoFocus
                className="w-full bg-transparent text-base placeholder:text-muted-foreground focus-visible:outline-none border-none focus:ring-0 focus:border-none"
                placeholder="Артикул детали"
                autoComplete="off"
                spellCheck="false"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && handleClose()}
              />
              {isMultiSearch ? (
                <button
                  onClick={handleMultiSearch}
                  disabled={isMultiSearchLoading}
                  className="inline-flex items-center justify-center rounded-md border text-xs p-1.5 hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                  title="Мультипоиск"
                >
                  {isMultiSearchLoading ? (
                    <div className="size-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
                  ) : (
                    <Layers className="size-4" />
                  )}
                </button>
              ) : (
                searchInfo?.hasGlobalSearchOption && (
                  <button
                    onClick={handleGlobalSearch}
                    className="inline-flex items-center justify-center rounded-md border text-xs p-1.5 hover:bg-accent hover:text-accent-foreground"
                    title="Глобальный поиск"
                  >
                    <Globe className="size-4" />
                  </button>
                )
              )}
              <button
                onClick={handleClose}
                className="inline-flex items-center justify-center rounded-md border text-xs p-1.5 hover:bg-accent hover:text-accent-foreground"
              >
                Esc
              </button>
            </div>

            {isMultiSearchLoading && (
              <div className="mt-2">
                <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 animate-pulse"></div>
                </div>
                <p className="text-center text-sm text-green-600 mt-1">Выполняется мультипоиск...</p>
              </div>
            )}

            {query.trim() && !isMultiSearchLoading && (
              <div className="relative mt-2 max-h-[460px] overflow-y-auto border-t pt-2" role="listbox">
                {/* Обычный поиск */}
                {!isMultiSearch && (
                  <>
                    {!searchCompleted && <p className="text-center text-muted-foreground">...</p>}
                    
                    {!loading && searchCompleted && results.length === 0 && (
                      <p className="text-center text-muted-foreground">Ничего не найдено</p>
                    )}
                    
                    {!loading &&
                      results.map((item) => (
                        <div
                          key={item.uniqueId}
                          className="rounded-lg px-2 text-sm cursor-pointer hover:bg-gray-100 hover:text-accent-foreground"
                          onClick={() => handleSelectProduct(item)}
                        >
                          <div className="flex min-h-10 flex-row items-center gap-3">
                            <p className="basis-1/6 truncate text-stone-700/50">{item.code}</p>
                            <p className="basis-2/4 truncate">{item.brandName}</p>
                            <p className="basis-2/4 text-right truncate text-stone-700/25">{item.name}</p>
                          </div>
                        </div>
                      ))}
                  </>
                )}

                {/* Мультипоиск - выбор запчастей */}
                {isMultiSearch && Object.keys(multiSearchResults).length > 0 && (
                  <div className="space-y-4 pb-20">
                    {Object.keys(multiSearchResults).map((term) => (
                      <div key={term} className="border rounded-lg p-3">
                        <h3 className="font-semibold text-sm mb-2">{term}</h3>
                        <div className="space-y-1">
                          {multiSearchResults[term].map((item) => (
                            <div
                              key={item.uniqueId}
                              className={`rounded px-2 py-1 text-sm cursor-pointer ${
                                selectedParts[term] === item.uniqueId
                                  ? 'bg-gray-200 text-gray-800 border border-gray-300'
                                  : 'hover:bg-gray-100'
                              }`}
                              onClick={() => handleSelectPart(term, item.uniqueId)}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full border-2 ${
                                  selectedParts[term] === item.uniqueId
                                    ? 'bg-black border-black'
                                    : 'border-gray-300'
                                }`}></div>
                                <span className="font-medium">{item.brandName}</span>
                                <span className="text-gray-500">{item.code}</span>
                                {item.name && <span className="text-gray-400 text-xs">{item.name}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Фиксированная кнопка "Найти" для мультипоиска */}
            {isMultiSearch && Object.keys(multiSearchResults).length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 bg-white border-t p-4">
                <button
                  onClick={handleProceedToMultiSearch}
                  disabled={!isAllPartsSelected()}
                  className={`w-full py-3 px-4 rounded-md text-sm font-medium ${
                    isAllPartsSelected()
                      ? 'bg-gray-800 text-white hover:bg-gray-900'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isAllPartsSelected() ? 'Найти' : 'Выберите по одной запчасти для каждого артикула'}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchDialog;
